// ─────────────────────────────────────────────────────────────────────────────
// RF-043 — Puerta ÚNICA de visibilidad de la racha.
//
// El Admin Global puede apagar la funcionalidad de racha desde Gestión (`streakEnabled`). El RF
// exige que entonces NO aparezca en campaña; hasta S4 el backend detenía el cierre semanal pero el
// contrato no llevaba el interruptor, así que el widget se seguía pintando con los últimos valores
// calculados — justo lo que el RF prohíbe.
//
// Cualquier consumidor de la racha (widget, tracker, tira del Perfil, KPI de Innovación, insignias
// de la Biblioteca de Fundamentales) pasa por AQUÍ en vez de repetir la comprobación. El motivo no es
// estético: la comprobación tiene una forma correcta y una incorrecta que se parecen mucho, y
// escrita seis veces basta con equivocarse una para dejar una pantalla pintando la racha apagada.
// ─────────────────────────────────────────────────────────────────────────────
import { useMyStreak } from '@/api/recognition';
import type { StreakDto } from '@/api/recognition';

/**
 * ¿Puede pintarse la racha de este usuario?
 *
 * `habilitada === false` y NO `!habilitada`, a propósito: el campo es aditivo y un backend anterior
 * a S4 no lo emite. Con la forma negada, `undefined` apagaría la racha en TODOS los despliegues
 * mientras el backend viejo siga en pie —un despliegue escalonado bastaría—, que es un fallo peor
 * que el que RF-043 cierra. Se oculta ante el `false` explícito, nunca ante la ausencia del campo.
 */
export function isStreakVisible(streak: StreakDto | undefined | null): streak is StreakDto {
  return Boolean(streak) && streak!.habilitada !== false;
}

/**
 * `useMyStreak` con la puerta de RF-043 ya aplicada: devuelve la racha SOLO si puede pintarse, y
 * `undefined` si no hay datos todavía o si la funcionalidad está apagada.
 *
 * Devolver `undefined` en los dos casos es deliberado: los consumidores ya tenían que tratar "aún
 * no hay datos" (el endpoint puede no responder sin romper la vista, ver `useMyStreak`), así que
 * apagar la racha no añade ninguna rama nueva a ninguna pantalla.
 *
 * @param enabled se propaga tal cual a `useMyStreak` — el Perfil difiere la consulta cuando se está
 * viendo el perfil de un compañero (la racha es solo del propio, REQ-120).
 */
export function useVisibleStreak(programa?: string | null, enabled = true): StreakDto | undefined {
  // Rama B — `programa` elige la racha (Fundamentales por defecto = el día a día y el Perfil, que no
  // cambia). La landing de Brillamos pasa 'Brillamos' para ver SU racha.
  const streak = useMyStreak(programa, enabled);
  return isStreakVisible(streak.data) ? streak.data : undefined;
}

/**
 * ¿Está encendida la funcionalidad de racha? Responde SOLO al interruptor, sin exigir datos de racha
 * propios.
 *
 * Existe para el único caso en que los datos de racha que una pantalla pinta NO salen de
 * `GET v1/streaks/me`: la columna "Racha" de la vista de equipo (`metrics/TeamView`), que llega por
 * persona dentro de `useRecognitionTeam`. Ahí `useVisibleStreak` no sirve —no hay racha propia que
 * mostrar— pero el interruptor de RF-043 debe gobernar la columna igual.
 *
 * **Las dos formas de "no saber" NO son la misma, y por eso se tratan distinto:**
 *
 * - *Todavía cargando* → `false` (no se pinta). La respuesta viene en camino: esperar unos
 *   milisegundos no cuesta nada y evita el destello de datos de racha de terceros en la pantalla de
 *   un líder mientras el interruptor está apagado, que es justo lo que RF-043 prohíbe.
 * - *La consulta falló* → `true` (se pinta). Aquí ya no viene ninguna respuesta (`retry: 0` en
 *   `useMyStreak`), así que fallar hacia oculto dejaría al líder sin una columna de datos REALES
 *   —que llegaron por otro endpoint, sano— de forma permanente y por una causa ajena. Es la misma
 *   regla que `isStreakVisible`: no se apaga una funcionalidad por no haber podido leer el
 *   interruptor.
 *
 * La asimetría con la tarjeta KPI de Innovación es deliberada y no una incoherencia: allí los datos
 * que se pintan SON los de esta misma consulta, así que si falla no hay nada que enseñar y ocultar
 * no cuesta información; aquí los datos existen y son de otra fuente.
 *
 * NO uses esto para decidir si pintar un hueco de relleno ("—") a la espera de los datos: con la
 * racha apagada, ese hueco sigue mencionándola. Si lo que pintas SON los datos de la racha propia,
 * usa `useVisibleStreak`.
 *
 * Comparte la clave de consulta con `useVisibleStreak`, así que usar los dos en un mismo componente
 * no dispara una segunda petición.
 */
export function useStreakEnabled(enabled = true): boolean {
  // La columna "Racha" de Mi Equipo muestra la de Fundamentales (el día a día), como el Perfil.
  const streak = useMyStreak(null, enabled);
  if (streak.data) return streak.data.habilitada !== false;
  return streak.isError;
}
